import { setEncounterArena, siegeBossId } from '../../content/bosses';
import { SIEGE_THRONE_RING } from '../../content/zones/siege';
import { type V2, angleTo, dist } from '../../core/math';
import { rollLoot } from '../../game/loot';
import type { ZoneOpts } from '../../game/netHooks';
import type { SimEvent } from '../../sim/events';
import { getBossScript } from '../../sim/ai/brains';
import { registry } from '../../sim/registry';
import type { ZoneLayout } from '../../zones/types';
import { despawnEncounter } from './colossus';
import { type DeathView, type HudAction, type HudView, type InstanceEnv, clockText, emptyHud, once } from './env';
import { RAID_GATES, type Raid, type SiegeMode, gateClaim, gatesCleared, recordGate, siegeMode } from './lockouts';
import { addItems, emptyDrop, grantDrop } from './rewards';
import { GATE } from './rules';
import { InstanceScript, type ScriptState, str } from './script';

/**
 * DEEPVAULT (D8, ≈ Abyssal Dungeon) and WARLORD SIEGE (D9, ≈ Legion Raid)
 * gates (research 01 §4, 02 §5.3, §5.7–5.8, §11.8). Each gate zone runs one
 * boss encounter:
 * - The boss spawns when the party walks into the arena (level 30, arena
 *   centre / radius matched to the layout's mechanic spawn points).
 * - Enrage: the boss script's soft enrage, then a berserk wipe
 *   `GATE.berserkGrace` seconds later.
 * - Revives: none in the Deepvault and Siege Hard; Siege Normal gives each
 *   player one revive per attempt. When everyone is down the gate wipes and
 *   restarts: the encounter despawns and the party regroups at the resupply
 *   station (which restocks battle items between attempts).
 * - Clear: `<zone>_cleared` opens the next gate's portal (or the exit), the
 *   weekly lockout is recorded per character (Siege: Normal/Hard share it and
 *   the first clear locks the week's difficulty), gold / tokens are paid, and
 *   an optional Bonus Chest can be bought with gold.
 */

export interface BonusChest {
  gold: number;
  items: { id: string; count: number }[];
}

export interface GateDef {
  zone: string;
  raid: Raid;
  gate: number;
  title: string;
  bosses: { id: string; spawn: string }[];
  /** Arena centre / radius used by the boss mechanics (derived from the layout's spawn points). */
  arena(layout: ZoneLayout): { center: V2; radius: number };
  next: string | null;
  bonus: Record<SiegeMode, BonusChest>;
}

const sp = (layout: ZoneLayout, name: string): V2 => layout.spawnPoints[name] ?? { x: 0, z: 0 };

/** Centre of a ring of spawn points (`<prefix>_0..n-1`). */
function ringCentre(layout: ZoneLayout, prefix: string, n: number): V2 {
  let x = 0;
  let z = 0;
  for (let i = 0; i < n; i++) {
    const p = sp(layout, `${prefix}_${i}`);
    x += p.x;
    z += p.z;
  }
  return { x: x / n, z: z / n };
}
const bonus = (gold: number, items: [string, number][]): BonusChest => ({ gold, items: items.map(([id, count]) => ({ id, count })) });

export const GATES: Record<string, GateDef> = {
  deepvault_1: {
    zone: 'deepvault_1',
    raid: 'deepvault',
    gate: 1,
    title: 'Gate I — The Drowned Warden',
    bosses: [{ id: 'drowned_warden', spawn: 'boss' }],
    // Tidal pillars rise at half the arena radius around its centre: the layout's `pillar_*` ring.
    arena: (l) => {
      const center = ringCentre(l, 'pillar', 4);
      return { center, radius: 2 * dist(sp(l, 'pillar_0'), center) };
    },
    next: 'deepvault_2',
    bonus: { normal: bonus(100, [['vault_token', 2], ['leap_crystal', 10]]), hard: bonus(100, [['vault_token', 2], ['leap_crystal', 10]]) },
  },
  deepvault_2: {
    zone: 'deepvault_2',
    raid: 'deepvault',
    gate: 2,
    title: 'Gate II — The Tide Siren',
    bosses: [{ id: 'tide_siren', spawn: 'boss' }],
    // Thralls rise at 90% of the radius: the layout's `add_*` ring.
    arena: (l) => {
      const center = ringCentre(l, 'add', 6);
      return { center, radius: dist(sp(l, 'add_0'), center) / 0.9 };
    },
    next: null,
    bonus: { normal: bonus(150, [['vault_token', 3], ['leap_crystal', 15]]), hard: bonus(150, [['vault_token', 3], ['leap_crystal', 15]]) },
  },
  siege_1: {
    zone: 'siege_1',
    raid: 'siege',
    gate: 1,
    title: 'Gate I — The Twin Hounds',
    bosses: [
      { id: 'hellhound_kharos', spawn: 'boss_a' },
      { id: 'hellhound_vesh', spawn: 'boss_b' },
    ],
    arena: (l) => {
      const a = sp(l, 'boss_a');
      const b = sp(l, 'boss_b');
      return { center: { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 }, radius: 22 };
    },
    next: 'siege_2',
    bonus: { normal: bonus(150, [['warlord_relic', 4], ['catalyst', 2]]), hard: bonus(200, [['warlord_relic', 6], ['catalyst', 3]]) },
  },
  siege_2: {
    zone: 'siege_2',
    raid: 'siege',
    gate: 2,
    title: 'Gate II — The Hollow Throne',
    bosses: [{ id: 'vhalzeroth', spawn: 'boss' }],
    // Ring breaks are centred on the throne ring, not on the Tyrant's spawn.
    arena: () => ({ center: { x: SIEGE_THRONE_RING.x, z: SIEGE_THRONE_RING.z }, radius: SIEGE_THRONE_RING.outer }),
    next: 'siege_3',
    bonus: { normal: bonus(200, [['warlord_relic', 5], ['catalyst', 3]]), hard: bonus(300, [['warlord_relic', 8], ['catalyst', 4]]) },
  },
  siege_3: {
    zone: 'siege_3',
    raid: 'siege',
    gate: 3,
    title: 'Gate III — The Phantom Throne',
    bosses: [{ id: 'vhalzeroth_phantom', spawn: 'boss' }],
    // Phantom clones appear 1.5 m inside the radius: the layout's `edge_*` ring.
    arena: (l) => {
      const center = ringCentre(l, 'edge', 8);
      return { center, radius: dist(sp(l, 'edge_0'), center) + 1.5 };
    },
    next: null,
    bonus: { normal: bonus(300, [['warlord_relic', 6], ['catalyst', 4]]), hard: bonus(450, [['warlord_relic', 10], ['catalyst', 5]]) },
  },
};

export const RAID_NAMES: Record<Raid, string> = { deepvault: 'The Sunken Reliquary', siege: 'Warlord Siege' };
export const gateZone = (raid: Raid, gate: number): string => `${raid}_${gate}`;
export const ROMAN = ['', 'I', 'II', 'III', 'IV'];

export type GatePhase = 'ready' | 'fight' | 'wiped' | 'cleared';

export interface GateState extends ScriptState {
  phase: GatePhase;
  /** Attempt number (wipes restart the gate). */
  attempt: number;
  /** Seconds since the boss engaged. */
  time: number;
  /** Fight time at which the boss goes berserk (wipe). */
  berserk: number;
  /** Fight time of the soft enrage. */
  enrage: number;
  bossIds: number[];
  /** Seconds the whole party has been down. */
  deadFor: number;
  /** Seconds until a wiped gate is ready again. */
  wait: number;
  reason: '' | 'wipe' | 'berserk';
}

export class GateScript extends InstanceScript<GateState> {
  readonly def: GateDef;
  readonly mode: SiegeMode;

  constructor(env: InstanceEnv, opts: ZoneOpts, zone: string) {
    super(env, opts, { seq: 0, phase: 'ready', attempt: 1, time: 0, berserk: 0, enrage: 0, bossIds: [], deadFor: 0, wait: 0, reason: '' });
    const def = GATES[zone];
    if (!def) throw new Error(`No gate '${zone}'`);
    this.def = def;
    this.mode = def.raid === 'siege' && str(opts.diff) === 'hard' ? 'hard' : 'normal';
  }

  get flag(): string {
    return `${this.def.zone}_cleared`;
  }

  get hard(): boolean {
    return this.mode === 'hard';
  }

  /** Siege Normal lets each player revive once per attempt. */
  get revivesPerAttempt(): number {
    return this.def.raid === 'siege' && !this.hard ? GATE.siegeNormalRevives : 0;
  }

  private revKey(): string {
    return `${this.run}:${this.def.zone}:${this.state.attempt}:rev`;
  }

  myRevivesLeft(): number {
    return Math.max(0, this.revivesPerAttempt - (this.env.memo.get(this.revKey()) === true ? 1 : 0));
  }

  private bonusKey(): string {
    return `${this.run}:${this.def.zone}:bonus`;
  }

  arena(): { center: V2; radius: number } {
    return this.def.arena(this.z.layout);
  }

  // ---------------------------------------------------------------- lifecycle

  protected override init(): void {
    // The next gate's portal stays sealed until this run clears the gate.
    const p = this.env.progression;
    if (p) p.char.flags[this.flag] = false;
    const at = this.spawnPoint('resupply');
    const id = `inst_resupply_${this.def.zone}`;
    if (!this.z.interactables.some((i) => i.id === id)) {
      const it = { id, kind: 'station' as const, x: at.x + 1.6, z: at.z - 1.2, data: { station: 'Resupply' } };
      this.z.interactables.push(it);
      if (this.env.visual) this.z.addInteractMarker(it);
    }
  }

  // ---------------------------------------------------------------- host

  protected override hostTick(dt: number): void {
    const s = this.state;
    if (s.phase === 'ready') {
      const { center, radius } = this.arena();
      if (this.livingPlayers().some((p) => dist(p.pos, center) <= radius)) this.engage();
      return;
    }
    if (s.phase === 'wiped') {
      s.wait -= dt;
      if (s.wait <= 0) this.update({ phase: 'ready', wait: 0 });
      return;
    }
    if (s.phase !== 'fight') return;
    s.time += dt;
    if (s.time >= s.berserk) {
      this.wipe('berserk');
      return;
    }
    const all = this.players();
    if (all.length > 0 && all.every((p) => !p.alive)) {
      s.deadFor += dt;
      if (s.deadFor >= (this.revivesPerAttempt > 0 ? GATE.wipeGraceRevive : GATE.wipeGrace)) this.wipe('wipe');
    } else s.deadFor = 0;
  }

  protected override mirrorTick(dt: number): void {
    const s = this.state;
    if (s.phase === 'fight') s.time += dt;
    else if (s.phase === 'wiped') s.wait = Math.max(0, s.wait - dt);
  }

  /** Spawns the gate's boss(es) at level 30 and starts the encounter. */
  engage(): void {
    const { center, radius } = this.arena();
    const ids: number[] = [];
    let enrage = 0;
    for (const b of this.def.bosses) {
      const defId = this.def.raid === 'siege' ? siegeBossId(b.id, this.hard) : b.id;
      const at = this.spawnPoint(b.spawn);
      const boss = this.z.spawnMonster(defId, GATE.level, { x: at.x, z: at.z }, { facing: angleTo(at, center) });
      setEncounterArena(boss, center, radius);
      ids.push(boss.id);
      enrage = Math.max(enrage, getBossScript(defId)?.enrageAfter ?? 600);
    }
    this.update({ phase: 'fight', time: 0, deadFor: 0, bossIds: ids, enrage, berserk: enrage + GATE.berserkGrace, reason: '' });
  }

  protected override becameHost(): void {
    // Encounter state lives on the host's brain record: re-anchor the arena on the adopted bosses.
    const { center, radius } = this.arena();
    for (const id of this.state.bossIds) {
      const b = this.world.actors.get(id);
      if (b?.alive) setEncounterArena(b, center, radius);
    }
  }

  wipe(reason: 'wipe' | 'berserk'): void {
    for (const id of this.state.bossIds) {
      const a = this.world.actors.get(id);
      if (a) despawnEncounter(this.world, a);
    }
    this.update({ phase: 'wiped', reason, bossIds: [], attempt: this.state.attempt + 1, wait: GATE.resetDelay, deadFor: 0 });
  }

  protected override hostEvent(ev: SimEvent): void {
    const s = this.state;
    if (ev.type !== 'death' || s.phase !== 'fight' || !s.bossIds.includes(ev.id)) return;
    if (s.bossIds.every((id) => !this.world.actors.get(id)?.alive)) this.update({ phase: 'cleared' });
  }

  // ---------------------------------------------------------------- every peer

  protected override changed(prev: GateState, next: GateState): void {
    if (next.phase === prev.phase) return;
    switch (next.phase) {
      case 'fight': {
        const names = this.def.bosses.map((b) => registry.monsters.get(b.id)?.name ?? b.id).join(' & ');
        this.env.announce(`${ROMAN[this.def.gate]} · ${names}`, 'boss');
        break;
      }
      case 'wiped':
        this.env.announce(next.reason === 'berserk' ? 'The boss goes berserk — the raid is wiped out' : 'The raid has fallen — regroup at the resupply point', 'warning');
        this.regroup();
        break;
      case 'ready':
        if (prev.phase === 'wiped') this.env.toast('The gate is ready — engage when prepared', '#9ad8ff');
        break;
      case 'cleared':
        this.env.announce(`${this.def.title.split(' — ')[0] ?? 'Gate'} cleared!`, 'success');
        if (this.me && !this.me.alive) this.reviveMe(1);
        this.reward();
        break;
      default:
        break;
    }
  }

  /** After a wipe: back to the resupply point, alive, full HP, clean slate. */
  private regroup(): void {
    const me = this.me;
    if (!me) return;
    me.statuses = [];
    me.statsDirty = true;
    this.reviveMe(1, this.spawnPoint('resupply'));
    me.hp = me.stats.maxHp;
  }

  private reward(): void {
    const p = this.env.progression;
    if (!p) return;
    p.setFlag(this.flag);
    if (!once(this.env, `${this.run}:${this.def.zone}:reward`)) return;
    this.env.syncPeriods();
    const { raid, gate } = this.def;
    p.char.counters[`${raid}_gate${gate}`] = (p.char.counters[`${raid}_gate${gate}`] ?? 0) + 1;
    const claim = recordGate(p.char, p.account, raid, gate, this.mode);
    if (claim !== 'ok') {
      const locked = siegeMode(p.char, p.account) === 'hard' ? 'Hard' : 'Normal';
      this.env.toast(claim === 'cleared' ? `Gate ${ROMAN[gate]} was already cleared this week — no rewards` : `Your Siege is locked to ${locked} this week — no rewards`, '#e5a24d');
      this.env.save();
      return;
    }
    const table = raid === 'deepvault' ? `deepvault_${gate}` : `siege_${this.mode}_${gate}`;
    const drop = rollLoot(table, { level: GATE.level, rng: p.rng });
    this.env.memo.set(this.bonusKey(), 'offer');
    const sub = `${RAID_NAMES[raid]}${raid === 'siege' ? ` · ${this.hard ? 'Hard' : 'Normal'}` : ''} · ${clockText(this.state.time)}`;
    this.env.rewards(grantDrop(p, drop, this.def.title, `${this.def.title}`, sub));
    this.env.toast(`A Bonus Chest is on offer for ${this.bonusChest().gold} gold`, '#ffd24a');
    this.env.save();
  }

  bonusChest(): BonusChest {
    return this.def.bonus[this.mode];
  }

  /** Buys the gate's Bonus Chest with gold (once, after a rewarded clear). */
  buyBonus(): boolean {
    const p = this.env.progression;
    if (!p || this.env.memo.get(this.bonusKey()) !== 'offer') return false;
    const chest = this.bonusChest();
    if (p.account.wallet.gold < chest.gold) {
      this.env.toast(`You need ${chest.gold} gold for the Bonus Chest`, '#e5484d');
      return false;
    }
    p.account.wallet.gold -= chest.gold;
    this.env.memo.set(this.bonusKey(), 'bought');
    const drop = emptyDrop();
    for (const it of chest.items) addItems(drop, it.id, it.count);
    this.env.rewards(grantDrop(p, drop, 'Bonus Chest', 'Bonus Chest', `${this.def.title} · −${chest.gold} gold`));
    p.emit('wallet', { silver: p.account.wallet.silver, gold: p.account.wallet.gold });
    this.env.save();
    return true;
  }

  // ---------------------------------------------------------------- views

  view(): HudView {
    const s = this.state;
    const { raid, gate } = this.def;
    const title = raid === 'siege' ? `Warlord Siege · ${this.hard ? 'Hard' : 'Normal'}` : 'Sunken Reliquary';
    const v = emptyHud(raid, title, this.def.title);
    const p = this.env.progression;
    const done = p ? gatesCleared(p.char, p.account, raid) : [];
    for (let g = 1; g <= RAID_GATES[raid]; g++) {
      const state = g === gate ? (s.phase === 'cleared' ? 'done' : 'current') : done.includes(g) || g < gate ? 'done' : 'todo';
      v.pips.push({ label: ROMAN[g] ?? String(g), state });
    }
    if (s.phase === 'fight') {
      const toEnrage = s.enrage - s.time;
      v.timer = toEnrage > 0 ? { label: 'Enrage in', seconds: toEnrage, warn: toEnrage < 60 } : { label: 'Berserk in', seconds: s.berserk - s.time, warn: true };
    } else if (s.phase === 'wiped') v.timer = { label: 'Regrouping', seconds: s.wait, warn: false };
    if (this.revivesPerAttempt > 0) v.revives = { n: this.myRevivesLeft(), max: this.revivesPerAttempt, label: 'Your revive' };
    else v.badges.push({ text: 'No revives', tone: 'red' });
    if (this.hard) v.badges.push({ text: 'Hard', tone: 'red' });
    if (s.phase === 'ready') v.badges.push({ text: s.attempt > 1 ? `Attempt ${s.attempt}` : 'Enter the arena to begin', tone: 'blue' });
    if (s.phase === 'cleared') v.badges.push({ text: 'Cleared', tone: 'green' });
    else if (p && gateClaim(p.char, p.account, raid, gate, this.mode) !== 'ok') v.badges.push({ text: 'No rewards this week', tone: 'grey' });
    const chest = this.env.memo.get(this.bonusKey());
    if (chest === 'offer') v.actions.push({ id: 'bonus', label: `Bonus Chest · ${this.bonusChest().gold} gold`, tone: 'gold', confirm: `Buy the Bonus Chest for ${this.bonusChest().gold} gold? (${this.bonusChest().items.map((i) => `${i.count}× ${i.id.replace(/_/g, ' ')}`).join(', ')})` });
    v.actions.push(s.phase === 'fight' ? this.leaveAction('Leave the raid? The gate continues without you.') : this.leaveAction());
    return v;
  }

  override deathView(): DeathView {
    const s = this.state;
    const left = this.myRevivesLeft();
    const everyone = this.players().every((p) => !p.alive);
    const grace = this.revivesPerAttempt > 0 ? GATE.wipeGraceRevive : GATE.wipeGrace;
    const actions: HudAction[] = [];
    if (s.phase === 'fight' && left > 0) actions.push({ id: 'revive', label: `Revive (${left} left)`, tone: 'gold' });
    actions.push(this.leaveAction());
    return {
      title: 'You have fallen',
      text:
        s.phase !== 'fight'
          ? 'The encounter is over.'
          : left > 0
            ? 'Siege Normal: you may rise once per attempt.'
            : everyone
              ? 'The whole party is down — the gate restarts at the resupply point.'
              : 'No revives inside this gate. Wait for your party: if everyone falls, the gate restarts.',
      countdown: s.phase === 'fight' && everyone ? Math.max(0, grace - s.deadFor) : null,
      actions,
    };
  }

  override action(id: string): void {
    if (id === 'leave') this.env.leave();
    else if (id === 'bonus') this.buyBonus();
    else if (id === 'revive' && this.myRevivesLeft() > 0 && this.me && !this.me.alive && this.state.phase === 'fight') {
      this.env.memo.set(this.revKey(), true);
      this.reviveMe(GATE.reviveHp);
    } else if (id === 'resupply') {
      const p = this.env.progression;
      if (this.state.phase === 'fight') this.env.toast('The resupply station is sealed while the gate is in battle', '#e5484d');
      else if (p) {
        p.resetInstanceUses();
        const me = this.me;
        if (me?.alive) me.hp = me.stats.maxHp;
        this.env.toast('Resupplied: battle items restocked (swap your loadout in the Bag)', '#9be07a');
      }
    }
  }
}
